"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import ProcedureCardsView from "@/components/optilase/hub/procedure-cards-view";
import { surfaceFrom } from "@/components/optilase/surface";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";
import { regionFromPathname } from "@/lib/region";
import { resolveLink, resolveRichTextHref, type StoryblokLink } from "@/lib/storyblok-link";
import { isPresent } from "@/lib/sourced-blok";
import type { Region } from "@/lib/region";
import { usePublicPathname } from "@/lib/public-pathname";

type Asset = { filename?: string; alt?: string; focus?: string };

type TreatmentHero = { component?: string; image?: Asset; title?: string };

type TreatmentStory = {
  full_slug?: string;
  content?: {
    card_title?: string;
    card_image?: Asset;
    card_description?: string;
    card_link_label?: string;
    body?: TreatmentHero[];
  };
};

type ImageLinkCard = SbBlokData & {
  treatment?: string | TreatmentStory | null;
  image?: Asset;
  title?: string;
  description?: string;
  link_label?: string;
  link?: StoryblokLink;
};

function fromTreatment(card: ImageLinkCard, region: Region) {
  const story = typeof card.treatment === "object" ? card.treatment : null;
  const content = story?.content;
  if (!content) return null;
  const hero = (content.body ?? []).find((b) => b.image?.filename || b.title);
  return {
    image: isPresent(content.card_image) ? content.card_image : hero?.image,
    title: content.card_title || hero?.title,
    description: content.card_description,
    linkLabel: content.card_link_label,
    href: story?.full_slug ? resolveRichTextHref(`/${story.full_slug}`, region) : undefined,
  };
}

type CardsFields = {
  title?: string;
  intro?: string;
  card_variant?: string;
  cards?: ImageLinkCard[];
  card_bg_color?: unknown;
  bg_color?: unknown;
};

type CardsBlok = SbBlokData & SourcedBlok<CardsFields>;

const FIELDS: (keyof CardsFields)[] = [
  "title",
  "intro",
  "card_variant",
  "cards",
  "card_bg_color",
  "bg_color",
];

export default function GlobalCardsWithImageAndLink({ blok }: { blok: CardsBlok }) {
  const region = regionFromPathname(usePublicPathname());
  const merged = mergeSourced(blok, FIELDS);

  return (
    <div {...storyblokEditable(blok)}>
      <ProcedureCardsView
        intro={merged.intro}
        variant={merged.card_variant === "person" ? "person" : "procedure"}
        heading={merged.title ?? ""}
        surface={surfaceFrom(merged.bg_color)}
        cardSurface={surfaceFrom(merged.card_bg_color)}
        cards={(merged.cards ?? []).map((card) => {
          const source = fromTreatment(card, region);
          const image = isPresent(card.image) ? card.image : source?.image;
          const link = resolveLink(card.link, region);
          return {
            image: image?.filename ?? "",
            imageAlt: image?.alt ?? "",
            imageFocus: image?.focus,
            title: card.title || source?.title || "",
            body: card.description || source?.description || "",
            linkLabel: card.link_label || source?.linkLabel || undefined,
            href: link.href === "#" ? (source?.href ?? "#") : link.href,
          };
        })}
      />
    </div>
  );
}
